import { createApi } from '@reduxjs/toolkit/query/react';
import isEmpty from 'lodash/isEmpty';
import baseQuery from './fetchBaseQuery';
import {
    AuthenticationRequirements,
    LoginRequest,
    LoginResponse,
    RedirectResponse,
} from '../types';
import { includes } from 'lodash';

export const encodeParam = (next: string | undefined) => {
    return isEmpty(next) ? '' : encodeURIComponent(next as string);
};

const api = createApi({
    reducerPath: 'loginApi',
    baseQuery,
    endpoints: build => ({
        passwordLogin: build.mutation<LoginResponse, LoginRequest>({
            query: ({ username, password, sessionType, mfaCode, next }) => ({
                url: `/auth/api/login?next=${encodeParam(next)}`,
                method: 'POST',
                body: {
                    username,
                    password,
                    sessionType,
                    mfaCode,
                },
            }),
            transformErrorResponse: (error: {
                status: number;
                originalStatus: number;
                data: unknown | string | { message: string };
            }): string => {
                const defaultErrorMessage = 'Error processing your login.';
                if (typeof error?.data === 'string' && error.originalStatus === 401) {
                    const isExpired = includes(error?.data, 'expired');
                    // This is the response for an invalid MFA code.
                    return isExpired
                        ? 'Your verification code has expired.'
                        : 'Failed to verify your code.';
                }
                // @ts-ignore
                return (error?.data?.message || defaultErrorMessage) as string;
            },
        }),
        fetchAuthenticationRequirements: build.mutation<AuthenticationRequirements, string>({
            query: username => ({
                url: `/auth/api/requirements`,
                method: 'POST',
                body: username,
            }),
        }),
        fetchSamlRedirectUrl: build.mutation<RedirectResponse, { username: string; next?: string }>(
            {
                query: ({ username, next }) => ({
                    url: `/api/sso/saml-redirect?nextPage=${encodeParam(next)}`,
                    method: 'POST',
                    body: username,
                }),
            }
        ),
    }),
});
export const {
    usePasswordLoginMutation,
    useFetchSamlRedirectUrlMutation,
    useFetchAuthenticationRequirementsMutation,
} = api;
export default api;
